import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import theme from './theme';
import PublicLayout from './layouts/PublicLayout';
import PortalLayout from './layouts/PortalLayout';
import RoleGuard from './layouts/RoleGuard';
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import AlumniPage from './pages/public/AlumniPage';
import ResultCheckPage from './pages/public/ResultCheckPage';
import LoginPage from './pages/auth/LoginPage';
import ChangePasswordPage from './pages/auth/ChangePasswordPage';
import DashboardPage from './pages/portal/dashboards/DashboardPage';
import NoticeListPage from './pages/portal/notices/NoticeListPage';
import StudentListPage from './pages/portal/students/StudentListPage';
import StudentBulkUploadPage from './pages/portal/students/StudentBulkUploadPage';
import TeacherListPage from './pages/portal/teachers/TeacherListPage';
import MarksPage from './pages/portal/marks/MarksPage';
import ResultPublishPage from './pages/portal/results/ResultPublishPage';
import MarksheetPage from './pages/portal/marksheets/MarksheetPage';
import AttendancePage from './pages/portal/attendance/AttendancePage';
import FeesPage from './pages/portal/fees/FeesPage';
import TimetablePage from './pages/portal/timetable/TimetablePage';
import AssessmentsPage from './pages/portal/assessments/AssessmentsPage';
import NotificationsPage from './pages/portal/notifications/NotificationsPage';
import AuditLogPage from './pages/portal/audit/AuditLogPage';
import ProfilePage from './pages/portal/profile/ProfilePage';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 0, refetchOnWindowFocus: false } },
});

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <BrowserRouter>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/alumni" element={<AlumniPage />} />
                <Route path="/result-check" element={<ResultCheckPage />} />
              </Route>

              <Route path="/login" element={<LoginPage />} />

              <Route path="/portal" element={<RoleGuard><PortalLayout /></RoleGuard>}>
                <Route index element={<DashboardPage />} />
                <Route path="change-password" element={<ChangePasswordPage />} />
                <Route path="notices" element={<NoticeListPage />} />
                <Route path="students" element={<RoleGuard allow={['ADMIN', 'PRINCIPAL']}><StudentListPage /></RoleGuard>} />
                <Route path="students/bulk" element={<RoleGuard allow={['ADMIN']}><StudentBulkUploadPage /></RoleGuard>} />
                <Route path="teachers" element={<RoleGuard allow={['ADMIN', 'PRINCIPAL']}><TeacherListPage /></RoleGuard>} />
                <Route path="marks" element={<MarksPage />} />
                <Route path="results" element={<RoleGuard allow={['PRINCIPAL']}><ResultPublishPage /></RoleGuard>} />
                <Route path="marksheets" element={<RoleGuard allow={['STUDENT']}><MarksheetPage /></RoleGuard>} />
                <Route path="attendance" element={<AttendancePage />} />
                <Route path="fees" element={<FeesPage />} />
                <Route path="timetable" element={<TimetablePage />} />
                <Route path="assessments" element={<AssessmentsPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="audit-log" element={<RoleGuard allow={['ADMIN', 'PRINCIPAL']}><AuditLogPage /></RoleGuard>} />
                <Route path="profile" element={<ProfilePage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </SnackbarProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
